import { stringify } from 'querystring';

import { useTranslator } from '@translator-app/react';
import { Button } from '~/components/button';
import Form from '~/components/form';
import Heading from '~/components/heading';
import InputError from '~/components/inputerror';
import InputWrapper from '~/components/inputwrapper';
import TextField from '~/components/textfield';
import { API_ROOT } from '~/constants';
import GoogleIcon from '~/icons/google.svg';
import { isValidEmail } from '~/utils/email';
import fetch from 'isomorphic-unfetch';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import urlJoin from 'url-join';

import { captureException } from '../../pages/_app';

interface LoginValues {
  email: string;
  password: string;
}

interface Props {
  onComplete?: (values: LoginValues) => void;
  variant?: 'default' | 'landing';
}

export const LoginForm: React.FC<Props> = (props) => {
  let router = useRouter();
  let { onComplete, variant = 'default' } = props;
  let { language } = useTranslator();
  let [error, setError] = useState('');
  let [hasPassword, setHasPassword] = useState(false);

  const redirectLink = React.useMemo(() => {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}/api/auth/callback?redirectTo=${
        router.query.redirectTo || '/'
      }`;
    }

    return '#';
  }, [router?.query?.redirectTo, typeof window]);

  const handleAuthRequest = async (values: LoginValues) => {
    try {
      if (hasPassword && values.password) {
        let response = await fetch('/api/auth/handle-password-login', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            email: values.email,
            password: values.password,
          }),
        });

        if (!response.ok) {
          throw new Error('Login failed');
        }

        // @ts-ignore
        window.location.href = router.query.redirectTo || '/';
      } else {
        let response = await fetch(urlJoin(API_ROOT, '/auth/magic/request'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            email: values.email,
            redirectUri: redirectLink,
            language: language || 'en',
          }),
        });

        if (!response.ok) {
          throw new Error('Login failed');
        }

        onComplete(values);
      }
    } catch (err) {
      captureException(err);
      setError('Login Failed');
    }
  };

  const handleHasPasswordCheck = async (email: string) => {
    try {
      let response = await fetch(urlJoin(API_ROOT, '/auth/password/has-password'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });

      if (!response.ok) {
        throw new Error('Could not fetch user has password');
      }

      let responseBody = await response.json();
      setHasPassword(!!responseBody?.hasPassword);
    } catch (err) {
      captureException(err);
      setHasPassword(false);
    }
  };

  return (
    <div>
      <Heading tag={variant === 'landing' ? 'h2' : 'h1'}>Login</Heading>
      <Form
        initialValues={{
          email: '',
          password: '',
        }}
        onSubmit={(values: LoginValues) => {
          return handleAuthRequest(values);
        }}
        onValidate={(values: LoginValues) => {
          let res: any = {};
          if (!isValidEmail(values.email)) {
            res.email = 'Invalid email';
          }
          return res;
        }}
      >
        {({ inputs, submit, values, errors, touched, isSubmitting }) => {
          return (
            <div>
              <InputWrapper label="Email" error={errors['email']} touched={touched['email']}>
                <TextField
                  placeholder="email"
                  value={values['email']}
                  error={touched['email'] && errors['email']}
                  onChange={inputs['email'].change}
                  disabled={isSubmitting}
                  onBlur={() => {
                    handleHasPasswordCheck(values['email']);
                    inputs['email'].blur();
                  }}
                />
              </InputWrapper>
              {hasPassword && (
                <InputWrapper label="Password" error={errors['password']} touched={touched['password']}>
                  <TextField
                    placeholder="password"
                    value={values['password']}
                    error={touched['password'] && errors['password']}
                    onChange={inputs['password'].change}
                    disabled={isSubmitting}
                    onBlur={inputs['password'].blur}
                    type="password"
                  />
                </InputWrapper>
              )}
              <div className="flex">
                <Button appearance="primary" onPress={submit} isLoading={isSubmitting} isFullSize>
                  Continue
                </Button>
              </div>

              <InputError touched={true}>{error}</InputError>
            </div>
          );
        }}
      </Form>

      <div className="flex items-center my-4">
        <div className="w-full bg-gray-200 mr-4" style={{ height: 2 }}></div>
        <div className="uppercase font-medium text-gray-600">or</div>
        <div className="w-full bg-gray-200 ml-4" style={{ height: 2 }}></div>
      </div>

      <div>
        <Button
          href={urlJoin(
            API_ROOT,
            `/auth/google/redirect?${stringify({
              redirectUri: redirectLink,
              language: language || 'en',
            })}`
          )}
        >
          <div className="flex items-center justify-between w-full">
            <div>
              <GoogleIcon className="w-4 h-4" />
            </div>
            <div className="w-full flex justify-center">Sign in with Google</div>
          </div>
        </Button>
      </div>
    </div>
  );
};
